import { ApprovedVendorAllocation, CalculatorType, InverterType, ProgramType, VendorEquipmentTypes } from 'types';
import type {
  AvlAllocationDetail,
  BulkUpdateApprovedVendorItemsRequest,
  BulkUpdateApprovedVendorItemsResponse,
  FeocDataItem,
  IsoDateString,
} from 'types';

import { api } from './api';

const tag = 'ApprovedVendorListAdmin' as const;

/**
 * Frontend-facing shape of an `approvedVendorList` item, mirroring the backend CRUD contract
 * (`api/src/schemas/approvedVendorList.ts`). Defined locally so this admin service does not
 * couple to the legacy `ApprovedVendorSettingValue` query shape. Once the shared
 * `ApprovedVendorItemDocument` type is exposed to the frontend, this can re-use its
 * `Clientize<...>` form instead of duplicating fields.
 */
export interface AvlItemHistoryChange {
  from: unknown;
  to: unknown;
}

export interface AvlItemHistoryEntry {
  changedBy: string;
  /** Enriched display name resolved server-side (parallels checklist-templates `createdByName`). */
  changedByName?: string;
  changedAt: string;
  changes: Record<string, AvlItemHistoryChange>;
  note?: string;
}

export interface AvlAlias {
  source: string;
  value: string;
}

export interface AvlItem {
  id: string;
  equipmentType: VendorEquipmentTypes;
  manufacturer: string;
  modelNumber: string;
  name: string;
  catalogKey?: string;
  /** catalogKey captured at migration/creation; immutable troubleshooting breadcrumb. */
  legacyCatalogKey?: string;
  programTypes: ProgramType[];
  aliases: AvlAlias[];
  legacyIds: string[];
  allocation: ApprovedVendorAllocation;
  /** Active + archived allocation windows; the server always returns this (defaults to `[]`). */
  allocationDetails: AvlAllocationDetail[];
  needsApproval: boolean;
  sku?: string;
  description?: string;
  category?: string;
  calculatorType?: CalculatorType;
  inverterType?: InverterType;
  domesticContentSupported?: boolean;
  domesticContentPercentageMlpe?: number;
  domesticContentPercentageString?: number;
  feocPercentageMlpe?: number;
  feocPercentageString?: number;
  maxDCkW?: number;
  maxACkW?: number;
  maxPrice?: number;
  requiresExtendedWarranty?: boolean;
  extendedWarrantyCost?: number;
  omnidianAlias?: string;
  arbitrage?: boolean;
  backup?: boolean;
  bessDomesticContentPercentage?: number;
  bessFeocPercentage?: number;
  gridPairedACkW?: number;
  gridPairedPeakACkW?: number;
  usableCapacityKWh?: number;
  size?: { value: number; unit: 'kW' | 'kWh' | 'watts' };
  feocData?: FeocDataItem[];
  history: AvlItemHistoryEntry[];
  meta?: {
    createdAt?: string;
    modifiedAt?: string;
    createdBy?: string;
    modifiedBy?: string;
  };
  createdByName?: string;
  modifiedByName?: string;
}

export interface AvlItemListResponse {
  data: AvlItem[];
  total: number;
}

export interface ListAvlItemsParams {
  equipmentType?: VendorEquipmentTypes;
  programType?: ProgramType;
  allocation?: ApprovedVendorAllocation;
  manufacturer?: string;
  search?: string;
  /** Omit to fetch every match; the server otherwise paginates. */
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  /** Scope org-allocation filtering by org alias; ignored server-side when accountId is set. */
  orgAlias?: string;
  /** Scope by account; also drives the server's effectiveDate default (the account's contract-signed date). */
  accountId?: string;
  /** Server derives this from `accountId` when omitted — never set this from the client. */
  effectiveDate?: IsoDateString;
}

/**
 * Writable fields shared by create and update.
 *
 * Optional scalar/enum/object fields are `T | null` (not just `T | undefined`): the server
 * distinguishes "field omitted" (left alone) from "field explicitly cleared" (`null`, unset in
 * Mongo) — see the PUT route's field partitioning. Booleans and `feocData` are always sent with a
 * concrete value (`false` / `[]`), never `null`, since they have no "unset" state distinct from
 * their empty value.
 */
export interface AvlItemWritableFields {
  equipmentType: VendorEquipmentTypes;
  manufacturer: string;
  modelNumber: string;
  name: string;
  programTypes: ProgramType[];
  aliases: AvlAlias[];
  /** Only sent on create — PUT 400s if included; use `newAllocationDetails` to change it after. */
  allocation?: ApprovedVendorAllocation;
  needsApproval: boolean;
  sku?: string | null;
  description?: string | null;
  category?: string | null;
  calculatorType?: CalculatorType | null;
  inverterType?: InverterType | null;
  domesticContentSupported: boolean;
  domesticContentPercentageMlpe?: number | null;
  domesticContentPercentageString?: number | null;
  feocPercentageMlpe?: number | null;
  feocPercentageString?: number | null;
  maxDCkW?: number | null;
  maxACkW?: number | null;
  maxPrice?: number | null;
  requiresExtendedWarranty: boolean;
  extendedWarrantyCost?: number | null;
  omnidianAlias?: string | null;
  arbitrage: boolean;
  backup: boolean;
  bessDomesticContentPercentage?: number | null;
  bessFeocPercentage?: number | null;
  gridPairedACkW?: number | null;
  gridPairedPeakACkW?: number | null;
  usableCapacityKWh?: number | null;
  size?: { value: number; unit: 'kW' | 'kWh' | 'watts' } | null;
  feocData: FeocDataItem[];
}

export type CreateAvlItemRequest = AvlItemWritableFields;

export interface NewAllocationDetailInput {
  startDate: IsoDateString;
  allocationStatus: ApprovedVendorAllocation;
}

export interface UpdateAvlItemRequest {
  id: string;
  data: AvlItemWritableFields & {
    note?: string;
    newAllocationDetails?: NewAllocationDetailInput[];
    /** The item's `meta.modifiedAt` as loaded by the client; the server 409s on a stale value. */
    modifiedAt: string | null;
  };
}

export const approvedVendorListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAvlItems: build.query<AvlItemListResponse, ListAvlItemsParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.equipmentType) searchParams.set('equipmentType', params.equipmentType);
        if (params.programType) searchParams.set('programType', params.programType);
        if (params.allocation) searchParams.set('allocation', params.allocation);
        if (params.manufacturer) searchParams.set('manufacturer', params.manufacturer);
        if (params.search) searchParams.set('search', params.search);
        if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
        if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
        if (params.sortBy) searchParams.set('sortBy', params.sortBy);
        if (params.sortDir) searchParams.set('sortDir', params.sortDir);
        if (params.orgAlias) searchParams.set('orgAlias', params.orgAlias);
        if (params.accountId) searchParams.set('accountId', params.accountId);
        if (params.effectiveDate) searchParams.set('effectiveDate', params.effectiveDate);
        const qs = searchParams.toString();
        return { url: `/v2/approved-vendors${qs ? `?${qs}` : ''}` };
      },
      // Also tags 'ApprovedVendors' so an account equipment-exception change refetches these queries too.
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: tag, id })),
              { type: tag, id: 'LIST' },
              'ApprovedVendors' as const,
            ]
          : [{ type: tag, id: 'LIST' }, 'ApprovedVendors' as const],
    }),

    getAvlItem: build.query<AvlItem, string>({
      query: (id) => ({ url: `/v2/approved-vendors/${id}` }),
      providesTags: (_result, _error, id) => [{ type: tag, id }],
    }),

    createAvlItem: build.mutation<AvlItem, CreateAvlItemRequest>({
      query: (body) => ({
        url: '/v2/approved-vendors',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: tag, id: 'LIST' }],
    }),

    updateAvlItem: build.mutation<AvlItem, UpdateAvlItemRequest>({
      query: ({ id, data }) => ({
        url: `/v2/approved-vendors/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: tag, id },
        { type: tag, id: 'LIST' },
      ],
    }),

    bulkUpdateAvlItems: build.mutation<BulkUpdateApprovedVendorItemsResponse, BulkUpdateApprovedVendorItemsRequest>({
      query: (body) => ({
        url: '/v2/approved-vendors/bulk',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { ids }) => [
        ...ids.map((id) => ({ type: tag, id })),
        { type: tag, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAvlItemsQuery,
  useGetAvlItemQuery,
  useCreateAvlItemMutation,
  useUpdateAvlItemMutation,
  useBulkUpdateAvlItemsMutation,
} = approvedVendorListApi;
